import { computed, effect, Injectable, Injector, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { DialogActionBridgeImpl } from './dialog-action.bridge';
import { ActionButton, DialogConfig, DialogLayer, DialogMode, ResolvedButton } from '@/share/model/dialog.types';

@Injectable({ providedIn: 'root' })
export class DialogStackService {
    private idCounter = 0;

    readonly stack = signal<DialogLayer[]>([]);
    readonly isAnyOpen = computed(() => this.stack().length > 0);

    constructor(private readonly injector: Injector) {}

    push<T>(mode: DialogMode, config: DialogConfig<T>, actions: ActionButton[]): Observable<any> {
        const id = ++this.idCounter;
        const depth = this.stack().length;
        const closeSubject = new Subject<any>();
        const bridge = new DialogActionBridgeImpl();

        const layer: DialogLayer = {
            id,
            depth,
            mode,
            config: {
                ...config,
                data: { ...(config.data ?? {}), layerId: id, mode }
            } as DialogConfig<any>,
            actions: this._resolveButtons(actions),
            closeSubject,
            bridge,
            maximized: false,
            showLabel: false,
            zIndex: 5000 + depth * 100
        };

        setTimeout(() => {
            this.stack.update((s) => [...s, layer]);
            this._watchBridgeClose(id, bridge);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const firstBtn = document.querySelector<HTMLElement>(`[data-layer-id="${id}"] button:not([disabled])`);
                    firstBtn?.focus();
                });
            });
        }, 0);

        return closeSubject.asObservable();
    }

    close(id: number, result?: any): void {
        const layer = this.stack().find((l) => l.id === id);
        if (!layer) return;

        layer.bridge.reset();
        this.stack.update((s) => s.filter((l) => l.id !== id));

        queueMicrotask(() => {
            layer.closeSubject.next(result);
            layer.closeSubject.complete();
        });
    }

    closeTop(result?: any): void {
        const top = this.stack().at(-1);
        if (top) this.close(top.id, result);
    }

    getLayer(id: number): DialogLayer | undefined {
        return this.stack().find((l) => l.id === id);
    }

    toggleMaximize(id: number): void {
        this._updateLayer(id, (l) => ({ ...l, maximized: !l.maximized }));
    }
    toggleChild(id: number) {
        this._updateLayer(id, (l) => ({ ...l, config: { ...l.config, width: '90%', height: '90%' } }));
    }
    toggleLabel(id: number): void {
        this._updateLayer(id, (l) => ({ ...l, showLabel: !l.showLabel }));
    }

    setButtonLoading(action: string, loading: boolean, layerId?: number): void {
        this.stack.update((layers) =>
            layers.map((layer) => {
                const isTarget = layerId != null ? layer.id === layerId : layer === layers.at(-1);
                if (!isTarget) return layer;
                return {
                    ...layer,
                    actions: layer.actions.map((b) => (b.action === action ? { ...b, loading } : b))
                };
            })
        );
    }

    depthArray(depth: number): number[] {
        return Array.from({ length: depth }, (_, i) => i);
    }

    private _resolveButtons(raw: ActionButton[]): ResolvedButton[] {
        return raw.map((b) => ({
            ...b,
            safeIcon: b.icon,
            loading: b.loading ?? false
        }));
    }

    private _watchBridgeClose(id: number, bridge: DialogActionBridgeImpl): void {
        effect(
            () => {
                const req = bridge.closeRequest();
                if (req === null) return;
                this.close(id, req.result);
            },
            { injector: this.injector }
        );
    }

    private _updateLayer(id: number, updater: (l: DialogLayer) => DialogLayer): void {
        this.stack.update((s) => s.map((l) => (l.id === id ? updater(l) : l)));
    }
}
